import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, Card, CardContent,
  Grid, ToggleButton, ToggleButtonGroup, CircularProgress, Alert, Avatar, LinearProgress,
} from '@mui/material';
import { BoltOutlined, WarningAmberRounded } from '@mui/icons-material';

// The monthly compute-minutes quota is read live from GitLab via
// GET /api/builds/ci-minutes. There is no hardcoded fallback: if the real
// limit can't be read (token lacks namespace access, GitLab unreachable,
// self-managed instance with no cap, etc) the quota banner is simply hidden
// rather than shown against a guessed number.
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip,
  Legend, PointElement, LineElement,
} from 'chart.js';
import {
  Apple, Android, AccessTime, AutorenewRounded, ListAlt, Speed, ErrorOutline,
  Download, OpenInNew,
} from '@mui/icons-material';
import { useAppDetailsDrawer } from './AppDetailsDrawerContext';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, PointElement, LineElement);

interface BuildCostDashboardProps { isActive: boolean }

type TopAppItem = {
  applicationId: number;
  composerHost: string;
  composerAppId: number;
  platform: string;
  appName: string | null;
  total: number;
  success: number;
  failed: number;
  totalDurationSec: number;
  avgDurationSec: number;
  completedWithTiming: number;
};

type TopAppsResponse = {
  items: TopAppItem[];
  days: number;
  total: number;
  grandTotalBuilds?: number;
  grandTotalDurationSec?: number;
};

const formatMinutes = (sec: number): string => {
  if (sec <= 0) return '0 min';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatDuration = (sec: number): string => {
  if (sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const rem = sec % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
};

const platformIcon = (p: string) => {
  const pl = (p || '').toLowerCase();
  if (pl === 'ios') return <Apple sx={{ fontSize: 18 }} />;
  if (pl === 'android') return <Android sx={{ fontSize: 18 }} />;
  return null;
};

const platformColor = (p: string) => {
  const pl = (p || '').toLowerCase();
  if (pl === 'ios') return '#0f172a';
  if (pl === 'android') return '#3DDC84';
  return '#94a3b8';
};

const csvEscape = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const BuildCostDashboard: React.FC<BuildCostDashboardProps> = ({ isActive }) => {
  const { openAppDetails } = useAppDetailsDrawer();
  const { dashboardData, fetchDashboardData } = useAuth();
  const [windowDays, setWindowDays] = useState<7 | 30 | 90>(30);
  const [data, setData] = useState<TopAppsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Month-to-date pipeline minutes — fetched separately from the window-based
  // top-apps data so the quota banner stays month-accurate regardless of the
  // 7/30/90 day toggle the rest of the page uses.
  const [monthToDate, setMonthToDate] = useState<{ usedSec: number; days: number } | null>(null);
  // Real monthly compute-minutes limit for the project's namespace, fetched
  // live from GitLab (via the backend). null until loaded / when unavailable,
  // in which case the quota banner is hidden (there is no hardcoded fallback).
  const [ciLimit, setCiLimit] = useState<{ limit: number | null; namespace: string | null; source: string } | null>(null);

  // Top-apps fetch with graceful fallback.
  //
  // We prefer the optimised `/top-apps/digest` endpoint (KV-cached on the
  // worker, Redis-cached on the proxy — see earlier perf work). When that
  // path isn't available yet — typically right after a frontend deploy that
  // landed before the worker / backend deploy — we fall back to the original
  // `/top-apps`, which has been in production for months and serves the same
  // payload shape. Without this, the entire dashboard sits blank because the
  // digest URL 404s.
  const fetchTopApps = async (days: number, limit: number, signal?: AbortSignal): Promise<any> => {
    const tok = localStorage.getItem('token');
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
    try {
      const resp = await axios.get(`/api/builds/top-apps/digest?days=${days}&limit=${limit}`, { headers, signal });
      return resp.data;
    } catch (digestErr: any) {
      // Only fall back on a real upstream failure — never on aborts the
      // caller deliberately fired.
      if (axios.isCancel?.(digestErr) || digestErr?.name === 'CanceledError') throw digestErr;
      console.warn('[BuildCostDashboard] /top-apps/digest unavailable, falling back to /top-apps:', digestErr?.message);
      const resp = await axios.get(`/api/builds/top-apps?days=${days}&limit=${limit}`, { headers, signal });
      return resp.data;
    }
  };

  // Fetch the cost summary whenever the page becomes active or the window changes.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const fetchCost = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await fetchTopApps(windowDays, 500);
        if (!cancelled) setData(payload);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.name === 'CanceledError') return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load build duration data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCost();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, windowDays]);

  // Separate month-to-date fetch — bounded by the current calendar month, not
  // the user's 7/30/90 toggle. Re-runs once per active mount; daily granularity
  // is plenty for quota tracking. Sends ceil(daysSinceStartOfMonth) so the
  // worker's "last N days" filter aligns with the month boundary. Uses the
  // same digest-with-fallback path so the quota banner doesn't silently
  // stay empty when the digest endpoint isn't live yet.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const daysSinceStart = Math.max(1, Math.ceil((now.getTime() - startOfMonth.getTime()) / (24 * 60 * 60 * 1000)));
    fetchTopApps(daysSinceStart, 500)
      .then(payload => {
        if (cancelled) return;
        setMonthToDate({
          usedSec: payload?.grandTotalDurationSec ?? 0,
          days: daysSinceStart,
        });
      })
      .catch(() => { /* non-fatal — banner just hides until next refresh */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Real monthly minute LIMIT from GitLab (cached server-side for 6h). Fetched
  // once per active mount; failures are non-fatal — the banner just falls back
  // to the configured default quota.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const tok = localStorage.getItem('token');
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
    axios.get('/api/builds/ci-minutes', { headers })
      .then(resp => {
        if (cancelled) return;
        setCiLimit({
          limit: typeof resp.data?.limit === 'number' ? resp.data.limit : null,
          namespace: resp.data?.namespace ?? null,
          source: resp.data?.source ?? 'unknown',
        });
      })
      .catch(() => { /* non-fatal — fall back to default quota */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Per-day breakdown comes from the recentBuilds feed already in scope. Not
  // perfectly accurate for windows beyond the cached feed, but a useful
  // visualisation without a second worker call.
  useEffect(() => {
    if (isActive && (!dashboardData?.recentBuilds || dashboardData.recentBuilds.length === 0)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const dayBuckets = useMemo(() => {
    const map = new Map<string, { total: number; success: number; failed: number }>();
    const builds = (dashboardData?.recentBuilds || []) as any[];
    const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
    for (const b of builds) {
      const ts = b.dateTimeStamp || b.updatedAt || b.createdAt;
      if (!ts) continue;
      const t = new Date(ts).getTime();
      if (!Number.isFinite(t) || t < cutoff) continue;
      const d = new Date(t);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      const bucket = map.get(key) ?? { total: 0, success: 0, failed: 0 };
      bucket.total++;
      const s = (b.status || '').toLowerCase();
      if (/success/.test(s)) bucket.success++;
      else if (/fail|cancel/.test(s)) bucket.failed++;
      map.set(key, bucket);
    }
    return map;
  }, [dashboardData, windowDays]);

  const dailySeries = useMemo(() => {
    const out: Array<{ date: string; total: number; success: number; failed: number }> = [];
    const now = new Date();
    const cap = Math.min(windowDays, 60); // chart caps at 60 days
    for (let i = cap - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      const b = dayBuckets.get(key);
      out.push({ date: format(d, 'MMM dd'), total: b?.total || 0, success: b?.success || 0, failed: b?.failed || 0 });
    }
    return out;
  }, [dayBuckets, windowDays]);

  const items = data?.items || [];
  const totals = {
    builds: data?.grandTotalBuilds ?? 0,
    durationSec: data?.grandTotalDurationSec ?? 0,
    apps: data?.total ?? 0,
    success: items.reduce((s, i) => s + i.success, 0),
    failed: items.reduce((s, i) => s + i.failed, 0),
  };
  const avgDurationSec = totals.builds > 0 ? Math.round(totals.durationSec / totals.builds) : 0;

  // For the per-app top-N chart limit the visible bar count so the page reads
  // well — full sortable list lives in the table below.
  const topNForChart = items.slice(0, 10);

  const exportCsv = () => {
    const rows: string[] = ['Composer host,Platform,Total builds,Success,Failed,Total build time (min),Avg seconds/build'];
    items.forEach(i => {
      rows.push([
        i.composerHost,
        i.platform,
        i.total,
        i.success,
        i.failed,
        Math.round(i.totalDurationSec / 60),
        i.avgDurationSec,
      ].map(csvEscape).join(','));
    });
    const blob = new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `build-duration-${windowDays}d-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'composerHost',
      headerName: 'App / Host',
      flex: 1,
      minWidth: 260,
      renderCell: (p: GridRenderCellParams) => {
        const r = p.row as TopAppItem;
        const pl = (r.platform || '').toLowerCase();
        const isIOS = pl === 'ios';
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
            <Avatar sx={{ bgcolor: platformColor(r.platform), width: 32, height: 32, flexShrink: 0 }}>
              {platformIcon(r.platform)}
            </Avatar>
            <Box sx={{ minWidth: 0, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 }}>
                {r.composerHost}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {isIOS ? 'iOS' : pl === 'android' ? 'Android' : r.platform || '—'} · app #{r.composerAppId}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'total',
      headerName: 'Builds',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{(p.row as TopAppItem).total}</Typography>
      ),
    },
    {
      field: 'breakdown',
      headerName: 'Success / Failed',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const r = p.row as TopAppItem;
        return (
          <Stack direction="row" spacing={0.5}>
            <Chip size="small" label={`✓ ${r.success}`} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(22, 163, 74, 0.12)', color: '#15803d' }} />
            <Chip size="small" label={`✗ ${r.failed}`} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(220, 38, 38, 0.12)', color: '#b91c1c' }} />
          </Stack>
        );
      },
    },
    {
      field: 'totalDurationSec',
      headerName: 'Total build time',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value: any, row: any) => (row as TopAppItem).totalDurationSec,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
          {formatMinutes((p.row as TopAppItem).totalDurationSec)}
        </Typography>
      ),
    },
    {
      field: 'avgDurationSec',
      headerName: 'Avg / build',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {formatDuration((p.row as TopAppItem).avgDurationSec)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 64,
      minWidth: 64,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      filterable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Tooltip title="View app details" arrow>
            <IconButton
              size="small"
              onClick={() => {
                const r = p.row as TopAppItem;
                openAppDetails(r.composerHost, r.platform, r.composerAppId);
              }}
              sx={{
                color: 'primary.main',
                bgcolor: 'rgba(99, 102, 241, 0.08)',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.16)' },
              }}
            >
              <OpenInNew sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>FS AM Build Duration Dashboard</Typography>
          <Typography variant="body2" color="textSecondary">
            How long pipelines take — total and average build duration per app and per day, and which apps spend the most time building.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            data-tour="cost-window"
            size="small"
            exclusive
            value={windowDays}
            onChange={(_, v) => v && setWindowDays(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', px: 1.5, fontWeight: 600, border: '1px solid', borderColor: 'divider',
                '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.12)', color: '#4338ca', borderColor: '#6366f1', zIndex: 1 },
              },
            }}
          >
            <ToggleButton value={7}>7 days</ToggleButton>
            <ToggleButton value={30}>30 days</ToggleButton>
            <ToggleButton value={90}>90 days</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Export as CSV" arrow>
            <IconButton data-tour="cost-export" onClick={exportCsv} disabled={!items.length} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* GitLab CI minutes quota banner — always month-to-date, independent of the window toggle.
          Only rendered when the real monthly limit is available from GitLab; no hardcoded fallback. */}
      {(() => {
        const effectiveQuota = typeof ciLimit?.limit === 'number' ? ciLimit.limit : null;
        if (!effectiveQuota || effectiveQuota <= 0) return null;
        const usedMin = monthToDate ? Math.round(monthToDate.usedSec / 60) : 0;
        const remaining = effectiveQuota - usedMin;
        const usedPct = Math.min(100, Math.max(0, (usedMin / effectiveQuota) * 100));
        const now = new Date();
        const monthLabel = now.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const daysElapsed = monthToDate?.days ?? now.getDate();
        const daysRemaining = Math.max(0, daysInMonth - daysElapsed);
        const projectedMin = daysElapsed > 0 ? Math.round((usedMin / daysElapsed) * daysInMonth) : usedMin;
        const overQuota = remaining < 0;
        const critical = !overQuota && usedPct >= 90;
        const warning = !overQuota && !critical && usedPct >= 70;
        const accent = overQuota ? '#dc2626' : critical ? '#dc2626' : warning ? '#f59e0b' : '#16a34a';
        const bg = overQuota || critical
          ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.06) 0%, rgba(239, 68, 68, 0.03) 100%)'
          : warning
            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(251, 191, 36, 0.03) 100%)'
            : 'linear-gradient(135deg, rgba(22, 163, 74, 0.05) 0%, rgba(34, 197, 94, 0.03) 100%)';
        return (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: overQuota || critical ? 'rgba(220, 38, 38, 0.3)' : warning ? 'rgba(245, 158, 11, 0.3)' : 'divider',
              background: bg,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 280 } }}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: `${accent}1f`, color: accent }}>
                  {overQuota || critical ? <WarningAmberRounded /> : <BoltOutlined />}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Monthly pipeline-time budget
                    </Typography>
                    <Tooltip
                      title={`Monthly limit read live from GitLab${ciLimit?.namespace ? ` (namespace: ${ciLimit.namespace})` : ''}. Used minutes are estimated from builds recorded by this dashboard.`}
                    >
                      <Chip
                        size="small"
                        label="Live limit"
                        color="success"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    </Tooltip>
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: accent, lineHeight: 1.1 }}>
                    {loading ? '…' : overQuota
                      ? `${Math.abs(remaining).toLocaleString()} over`
                      : remaining.toLocaleString()
                    }
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.75 }}>
                      min {overQuota ? 'over budget' : 'remaining'}
                    </Typography>
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {usedMin.toLocaleString()} min used in {monthLabel} (day {daysElapsed} of {daysInMonth})
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: accent }}>
                    {usedPct.toFixed(1)}% of {effectiveQuota.toLocaleString()} min/month
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={usedPct}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'rgba(15, 23, 42, 0.06)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: accent,
                      borderRadius: 5,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                  {overQuota
                    ? `Already over this month's build-time budget — pause non-critical pipelines until ${monthLabel} resets.`
                    : critical
                      ? `Less than 10% of the monthly build-time budget left — ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} until reset. Schedule fewer non-critical deploys.`
                      : warning
                        ? `Approaching the monthly build-time budget — current pace projects ~${projectedMin.toLocaleString()} min by end of ${monthLabel}.`
                        : `Healthy usage. At this pace, ~${projectedMin.toLocaleString()} min by end of ${monthLabel}. ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in cycle.`}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        );
      })()}

      {/* Summary tiles — duration-first, matching the dashboard's focus */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <SummaryCard icon={<AccessTime />} label="Total build time" value={formatMinutes(totals.durationSec)} accent="#0ea5e9" hint={`~${Math.round(totals.durationSec / 3600)} hours of pipeline time`} loading={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard icon={<Speed />} label="Avg build duration" value={formatDuration(avgDurationSec)} accent="#8b5cf6" hint="Mean duration across all completed builds" loading={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard icon={<ListAlt />} label="Total builds" value={totals.builds.toLocaleString()} accent="#6366f1" hint={`Across ${totals.apps} app${totals.apps !== 1 ? 's' : ''}`} loading={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard
            icon={<ErrorOutline />}
            label="Failure rate"
            value={totals.builds > 0 ? `${Math.round((totals.failed / totals.builds) * 100)}%` : '—'}
            accent={totals.builds > 0 && (totals.failed / totals.builds) > 0.2 ? '#dc2626' : '#16a34a'}
            hint={`${totals.failed} failed · ${totals.success} success`}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Per-app build-duration chart */}
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <AccessTime sx={{ color: '#0ea5e9' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Top 10 apps by build duration</Typography>
            <Typography variant="caption" color="text.secondary">
              Sum of build durations (Σ finishedAt − startedAt) in the last {windowDays} days
            </Typography>
          </Box>
        </Stack>
        {loading && topNForChart.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : topNForChart.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">No builds in the selected window.</Typography>
          </Box>
        ) : (
          <Box sx={{ height: 320 }}>
            <Bar
              data={{
                labels: topNForChart.map(i => i.composerHost),
                datasets: [{
                  label: 'Minutes',
                  data: topNForChart.map(i => Math.round(i.totalDurationSec / 60)),
                  backgroundColor: topNForChart.map((_, idx) => `rgba(99, 102, 241, ${1 - (idx / topNForChart.length) * 0.5})`),
                }],
              }}
              options={{
                indexAxis: 'y',
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const item = topNForChart[ctx.dataIndex];
                        return `${ctx.parsed.x} min · ${item.total} builds · avg ${formatDuration(item.avgDurationSec)}`;
                      },
                    },
                  },
                },
                scales: {
                  x: { beginAtZero: true, ticks: { font: { weight: 'bold' } }, title: { display: true, text: 'Minutes' } },
                  y: { ticks: { font: { weight: 'bold' } } },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Daily series */}
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <AutorenewRounded sx={{ color: '#6366f1' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Builds per day</Typography>
            <Typography variant="caption" color="text.secondary">
              Daily build counts from the live activity feed (last {Math.min(windowDays, 60)} days)
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ height: 280 }}>
          <Bar
            data={{
              labels: dailySeries.map(d => d.date),
              datasets: [
                { label: 'Success', data: dailySeries.map(d => d.success), backgroundColor: '#16a34a', stack: 'a' },
                { label: 'Failed', data: dailySeries.map(d => d.failed), backgroundColor: '#dc2626', stack: 'a' },
                { label: 'Other', data: dailySeries.map(d => Math.max(0, d.total - d.success - d.failed)), backgroundColor: '#94a3b8', stack: 'a' },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              plugins: { legend: { position: 'top' } },
              scales: {
                x: { stacked: true, ticks: { maxRotation: 0, autoSkip: true } },
                y: { stacked: true, beginAtZero: true },
              },
            }}
          />
        </Box>
      </Paper>

      {/* Full table */}
      <Paper sx={{ ...dataGridContainerSx, height: 540 }}>
        {loading && items.length === 0 && (
          <Box sx={{ height: 4 }}><LinearProgress /></Box>
        )}
        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => row.applicationId}
          pageSizeOptions={[15, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 15 } },
            sorting: { sortModel: [{ field: 'totalDurationSec', sort: 'desc' }] },
          }}
          rowHeight={56}
          disableRowSelectionOnClick
          sx={dataGridSx}
        />
      </Paper>
    </Box>
  );
};

const SummaryCard: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string;
  accent: string;
  hint?: string;
  loading?: boolean;
}> = ({ icon, label, value, accent, hint, loading }) => (
  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, color: accent }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
        <Typography variant="overline" sx={{ fontWeight: 700, color: accent }}>{label}</Typography>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, mb: 0.5 }}>
        {loading ? '…' : value}
      </Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

export default BuildCostDashboard;
